import dev from '@nicolaudie-group/ng-ui-kit/helpers/dev'

/**
 * Clean and parse a beams array
 */
export const parseBeams = (rawBeams) => {
  dev.log('Parsing beams')
  // Parse the ids, because they are strings for whatever reason
  const beams = rawBeams.map((beam) => ({
    ...beam,
    id: parseInt(beam.id, 10),
  }))

  // This is the beams that share the same column, stored when
  // handling columns, to check if two beams are overlapping
  // later when handling rows
  // const beamsWithSameColumn = []

  // This is how much each beam must be moved to the right
  // because the previous ones that have moved
  let columnsOffset = 0
  // Sort the beams by their x (column) position
  beams.sort((beamA, beamB) => beamA.pos_x - beamB.pos_x)
  // For each beam
  for (let i = 0; i < beams.length; i += 1) {
    const beam = beams[i]
    /*
    const nextBeam = beams[i + 1]
    // If there is another beam after, that has the same column
    if (nextBeam && nextBeam.pos_x === beam.pos_x) {
      // Store the combination of beams with same column
      beamsWithSameColumn.push(`${beam.id}/${nextBeam.id}`)
    }
    */
    // Move it by the columns offset
    beam.pos_x += columnsOffset
    // Calculate the distance to the grid
    const distanceToGrid = beam.pos_x % 100
    if (distanceToGrid !== 0) {
      // If the beam is not at the position of a cell of the grid
      // Move the beam to the right until it's at the position of a cell of the grid
      beam.pos_x += (100 - distanceToGrid)
      // Move the following beams to the right by a column
      columnsOffset += 100
    }
  }

  // This is how much each beam must be moved to the bottom
  // because the previous ones that have moved
  let rowsOffset = 0
  // Sort the beams by their y (row) position
  beams.sort((beamA, beamB) => beamA.pos_y - beamB.pos_y)
  // For each beam
  for (let i = 0; i < beams.length; i += 1) {
    const beam = beams[i]
    /*
    const nextBeam = beams[i + 1]
    // If there is another beam after, that has the same row, and
    // that has the same column, then it means they're overlapping
    const beamsOverlapping = (
      nextBeam
      && nextBeam.pos_y === beam.pos_y
      && beamsWithSameColumn.includes(`${beam.id}/${nextBeam.id}`)
    )
    */
    // Move it by the rows offset
    beam.pos_y += rowsOffset
    // Calculate the distance to the grid
    const distanceToGrid = beam.pos_y % 100
    if (distanceToGrid !== 0) {
      // If the beam is not at the position of a cell of the grid
      // Move the beam to the bottom until it's at the position of a cell of the grid
      beam.pos_y += (100 - distanceToGrid)
      // Move the following beams to the right by a column
      rowsOffset += 100
    }
    /*
    if (beamsOverlapping) {
      // If the next beam is overlapping with this one
      // Move the next beam and the ones after
      rowsOffset += 100
    }
    */
  }

  return beams
}

/**
 * Counts the number of columns and rows that have a beam
 * It also includes the empty rows/columns at the top/left
 * of the grid
 */
export const countOccupied = (beams) => {
  const lastOccupied = {
    column: 0,
    row: 0,
  }

  for (let i = 0; i < beams.length; i += 1) {
    const beam = beams[i]
    if (beam.pos_x > lastOccupied.column) {
      lastOccupied.column = beam.pos_x
    }
    if (beam.pos_y > lastOccupied.row) {
      lastOccupied.row = beam.pos_y
    }
  }

  const columns = (lastOccupied.column / 100) + 1
  const rows = (lastOccupied.row / 100) + 1
  return {
    columns,
    rows,
  }
}

/**
 * Clone (deeply or not) the beams to prevent mutating original beams array
 * @param {import('@/components/Beams/beamsTypes').Beam[]} beams - Beams to clone
 * @
 * @returns {import('@/components/Beams/beamsTypes').Beam[]} Cloned beams
 */
export const cloneBeams = (beams, deep = false) => beams.map((beam) => {
  if (deep) {
    return { ...beam }
  }
  return beam
})

function getDistance(xa, ya, xb, yb) {
  return Math.sqrt((xb - xa) ** 2 + (yb - ya) ** 2)
}

function formatCoordinate(beam) {
  return `${beam.pos_x};${beam.pos_y}`
}

export function optimizeBeams(beams) {
  const firstBeam = beams[0] || { pos_x: 0, pos_y: 0 }
  let minX = firstBeam.pos_x
  let minY = firstBeam.pos_y
  let maxX = firstBeam.pos_x
  let maxY = firstBeam.pos_y
  const distanceCounts = {} // Tracks the number of times a distance is found between two beams
  let maxDistanceCount = 0
  let distanceMode = 0 // Distance that was found the most times
  let prevX = 0
  let prevY = 0

  for (let i = 0; i < beams.length; i += 1) {
    // This first loop is there to compute normalization parameters
    const { pos_x: x, pos_y: y } = beams[i]
    minX = x < minX ? x : minX
    minY = y < minY ? y : minY
    maxX = x > maxX ? x : maxX
    maxY = y > maxY ? y : maxY
    if (i > 0) {
      const distance = getDistance(prevX, prevY, x, y)
      const distanceCount = (distanceCounts[distance] || 0) + 1
      distanceCounts[distance] = distanceCount
      if (distanceCount > maxDistanceCount) {
        maxDistanceCount = distanceCount
        distanceMode = distance
      }
    }
    prevX = x
    prevY = y
  }
  const deltaX = maxX - minX
  const deltaY = maxY - minY
  const offsetX = (deltaX / 2) - maxX
  const offsetY = (deltaY / 2) - maxY
  const gridSize = Math.floor(distanceMode / 2)
  const normalizedIndexX = {}
  const normalizedIndexY = {}
  const result = beams.map((beam) => {
    // This second loop is to normalize the coordinates using the computed normalization parameters
    const { id, pos_x: x, pos_y: y } = beam
    const normalizedX = Math.round((x + offsetX) / gridSize) * gridSize
    const normalizedY = Math.round((y + offsetY) / gridSize) * gridSize
    const normalizedBeam = {
      id,
      pos_x: normalizedX,
      pos_y: normalizedY,
    }
    // Index the beams by their normalized X/Y coordinates, makes coordinates unique too
    normalizedIndexX[normalizedX] = [...(normalizedIndexX[normalizedX] || []), normalizedBeam]
    normalizedIndexY[normalizedY] = [...(normalizedIndexY[normalizedY] || []), normalizedBeam]
    return normalizedBeam
  })
  // Sort the normalized unique coordinates to convert them to rows/columns
  const sortedX = Object.keys(normalizedIndexX).sort((a, b) => a - b)
  const sortedY = Object.keys(normalizedIndexX).sort((a, b) => a - b)
  // Store a list of coordinates, to compare if some are doubled (overlapping beams)
  const beamsByCoordinates = {}
  // Mutate normalized & sorted coordinates to rows/columns
  for (let i = 0; i < sortedX.length; i += 1) {
    // Get the beams at the normalizedX coordinate
    const normalizedX = sortedX[i]
    const beamsAtNormalizedX = normalizedIndexX[normalizedX]
    for (let j = 0; j < beamsAtNormalizedX.length; j += 1) {
      // And for each beam at normalizedX coordinate, convert the coordinate to column
      const beamAtNormalizedX = beamsAtNormalizedX[j]
      beamAtNormalizedX.pos_x = i * 100
    }
  }
  for (let i = 0; i < sortedY.length; i += 1) {
    // Get the beams at the normalizedY coordinate
    const normalizedY = sortedY[i]
    const beamsAtNormalizedY = normalizedIndexY[normalizedY]
    for (let j = 0; j < beamsAtNormalizedY.length; j += 1) {
      // And for each beam at normalizedX coordinate, convert the coordinate to column
      const beamAtNormalizedY = beamsAtNormalizedY[j]
      beamAtNormalizedY.pos_y = i * 100

      /*
      Now since all beams coordinates are complete, we also take advantage of this loop to index
      beams by their coordinates so we can count them in order to detect and fix overlap
      */
      const formattedCoordinates = formatCoordinate(beamAtNormalizedY)
      beamsByCoordinates[formattedCoordinates] = beamsByCoordinates[formattedCoordinates] || []
      beamsByCoordinates[formattedCoordinates].push(beamAtNormalizedY)
    }
  }
  const uniqueCoordinates = Object.keys(beamsByCoordinates)
  /*
    Check if there are beams overlapping (they have the exact same coordinates).
    Since they'd have the same coordinates, we simply need to verify that each
    coordinate is unique = there is as many coordinates as there are beams
  */
  if (uniqueCoordinates.length !== result.length) {
    const overlappingCoordinates = uniqueCoordinates.filter((uniqueCoordinate) => (
      beamsByCoordinates[uniqueCoordinate].length > 1
    )).map((coordinateWithOverlap) => beamsByCoordinates[coordinateWithOverlap])
    dev.error(new Error(`Overlapping beams: ${JSON.stringify(overlappingCoordinates, undefined, 2)}`))
    // Offsets from which we can start adding the overlapping beams
    let lastOffsetX = -100
    const lastOffsetY = (sortedY.length + 1) * 100
    // Loop over the coordinates with overlapping beams
    for (let i = 0; i < overlappingCoordinates.length; i += 1) {
      const overlappingBeams = overlappingCoordinates[i]
      // Loop over the overlapping beams (start at 1 to leave the first overlapping beam in place)
      for (let j = 1; j < overlappingBeams.length; j += 1) {
        // Move beams following the first overlapping beam to the last row
        const overlappingBeam = overlappingBeams[j]
        // Increment the lastOffsetX to prevent overlapping the overlapping beams again :)
        lastOffsetX += 100
        overlappingBeam.pos_x = lastOffsetX // Move beam to lastOffsetX
        overlappingBeam.pos_y = lastOffsetY // Move beam after last row
      }
    }
  }
  return result
}

/**
 * Parse tabular data (ie. copied from Microsoft Excel/Google Sheets/Numbers) to a list of beams
 *
 * Format is:
 * ```
 * `${id1}\t${pos_x1}\t${pos_y1}
${id2}\t${pos_x2}\t${pos_y2}`
 * // etc...
 * ```
 * @param {String} coordinates - Tabular data to parse
 */
export const parseTabular = (coordinates) => (
  coordinates.split('\n').map((coordinate) => {
    const [id, pos_x, pos_y] = coordinate.split('\t').map((item) => (
      parseInt(item, 10)
    ))
    return {
      id, pos_x, pos_y,
    }
  })
)
